import { HttpErrorKind } from "@kalodata/http";
import type { HttpErrorShape, HttpExecutionContext, HttpRequestConfig } from "@kalodata/http";

import report from "@report";

import { resolveTraceIdFromHeaders } from "./traceId";
import type { ClipHttpContext, ClipHttpMeta } from "./types";

const HTTP_API_SUCCESS_EVENT = "http_api_success";
const HTTP_API_ERROR_EVENT = "http_api_error";
const HTTP_API_TIMEOUT_EVENT = "http_api_timeout";
type HttpExecutionContextWithOptionalTiming = HttpExecutionContext<ClipHttpMeta, ClipHttpContext> & {
  timing?: {
    durationMs?: number;
    requestEndAt?: number;
  };
};

const getRequestPath = (request?: HttpRequestConfig<unknown, ClipHttpMeta, ClipHttpContext>) => {
  const requestUrl = request?.url ?? "";
  if (!requestUrl) return "";

  try {
    const parsedUrl = requestUrl.startsWith("http") ? new URL(requestUrl) : new URL(requestUrl, window.location.origin);
    return parsedUrl.pathname;
  } catch {
    return requestUrl.split("?")[0];
  }
};

const getErrorMessage = (error: HttpErrorShape<unknown, ClipHttpMeta, ClipHttpContext>) => {
  const responseData = error.response?.data as { message?: string | null } | undefined;
  if (typeof responseData?.message === "string" && responseData.message.length > 0) {
    return responseData.message;
  }
  if (typeof error.message === "string" && error.message.length > 0) {
    return error.message;
  }
  return "unknown error";
};

const buildErrorMeta = (error: HttpErrorShape<unknown, ClipHttpMeta, ClipHttpContext>) => {
  const responseData = error.response?.data as { code?: string | number | null } | undefined;
  const resolvedCode =
    error.code ?? (responseData?.code !== undefined && responseData?.code !== null ? responseData.code : "");
  const traceId = error.traceId || resolveTraceIdFromHeaders(error.response?.headers);
  return {
    method: error.request?.method ?? "",
    status: error.status ?? "",
    code: resolvedCode,
    kind: error.kind ?? HttpErrorKind.Unknown,
    traceId,
  };
};

const reportHttpTimeout = (
  error: HttpErrorShape<unknown, ClipHttpMeta, ClipHttpContext>,
  context: HttpExecutionContextWithOptionalTiming,
) => {
  const requestPath = getRequestPath(error.request);
  report.event(HTTP_API_TIMEOUT_EVENT, {
    kalo_pos: HTTP_API_TIMEOUT_EVENT,
    kalo_type: requestPath,
    kalo_value: getErrorMessage(error),
    kalo_json: {
      ...buildErrorMeta(error),
      timeoutMs: error.request?.timeoutMs ?? "",
      elapsedMs: context.timing?.durationMs ?? "",
      requestEndAt: context.timing?.requestEndAt ?? "",
    },
  });
};

const reportHttpError = (
  error: HttpErrorShape<unknown, ClipHttpMeta, ClipHttpContext>,
  context: HttpExecutionContextWithOptionalTiming,
) => {
  report.event(HTTP_API_ERROR_EVENT, {
    kalo_pos: HTTP_API_ERROR_EVENT,
    kalo_type: getRequestPath(error.request),
    kalo_value: getErrorMessage(error),
    kalo_json: {
      ...buildErrorMeta(error),
      requestEndAt: context.timing?.requestEndAt ?? "",
      elapsedMs: context.timing?.durationMs ?? "",
    },
  });
};

export const reportHttpRequestError = (
  error: HttpErrorShape<unknown, ClipHttpMeta, ClipHttpContext>,
  context: HttpExecutionContextWithOptionalTiming,
) => {
  if (error.kind === HttpErrorKind.Timeout) {
    reportHttpTimeout(error, context);
    return;
  }
  reportHttpError(error, context);
};

export const reportHttpRequestSuccess = (
  request: HttpRequestConfig<unknown, ClipHttpMeta, ClipHttpContext>,
  traceId: string,
) => {
  report.event(HTTP_API_SUCCESS_EVENT, {
    kalo_pos: HTTP_API_SUCCESS_EVENT,
    kalo_type: getRequestPath(request),
    kalo_json: {
      traceId,
    },
  });
};
